/// <reference lib="dom" />

import type { Teacher } from '@timetable/types'
import type { SchedulePeriodWithContext } from './period-discovery'
import type { SubstitutionRules } from '@timetable/types'

// ============================================================================
// Types
// ============================================================================

export interface PriorityContext {
  period: SchedulePeriodWithContext
  rules: SubstitutionRules
  /** Only posted assignments — used for fairness sort (NOT draft batch) */
  postedAssignmentsByTeacher: Map<string, { daily: number; weekly: number }>
  /** Teacher IDs already assigned in the current engine run (for consecutive avoidance) */
  currentRunAssignedTeacherIds: Set<string>
  /** Teacher subjects map: teacherId → Set<subjectId> */
  teacherSubjects: Map<string, Set<string>>
  /** Teacher class associations: teacherId → Set<classSectionId> */
  teacherClassAssociations: Map<string, Set<string>>
  /** Teacher class group associations: teacherId → Set<classGroupId> */
  teacherClassGroupAssociations: Map<string, Set<string>>
}

/**
 * Score breakdown for a single candidate — used by the progress animation
 * to explain why a teacher was selected.
 */
export interface CandidateScoreBreakdown {
  teacher: Teacher
  score: number
  isSubjectMatch: boolean
  isClassAssociation: boolean
  isClassGroupAssociation: boolean
  isClassTeacherForSupervision: boolean
  isConsecutiveAvoidance: boolean
  dailyPostedCount: number
  weeklyPostedCount: number
}

export interface SortCandidatesResult {
  sorted: Teacher[]
  /** Score breakdowns for the top 3 candidates — used for progress animation */
  topBreakdowns: CandidateScoreBreakdown[]
}

// ============================================================================
// sortCandidates
// ============================================================================

/**
 * Sort candidates by priority (lower score = higher priority).
 * Uses a stable sort — equal-priority candidates preserve their relative order.
 *
 * Returns both the sorted array and score breakdowns for the top 3 candidates
 * so the progress animation can explain why each teacher was selected.
 */
export function sortCandidates(
  candidates: Teacher[],
  context: PriorityContext
): SortCandidatesResult {
  const {
    period,
    rules,
    postedAssignmentsByTeacher,
    currentRunAssignedTeacherIds,
    teacherSubjects,
    teacherClassAssociations,
    teacherClassGroupAssociations,
  } = context

  const subjectId = period.subject_id
  const classSectionId = period.class_section_id
  // class_group is a string field on the classes table (the group name), not a relation
  const classGroupId = period.class_section?.class?.class_group ?? null
  const classTeacherId = period.class_section?.class_teacher_id ?? null
  const isBreakOrSupervision =
    period.period.is_break === true || period.period.break_type != null

  // Compute score + breakdown for each candidate (lower score = higher priority)
  const breakdowns = new Map<string, CandidateScoreBreakdown>()

  for (const teacher of candidates) {
    const tid = teacher.id
    let score = 0

    let isClassTeacherForSupervision = false
    let isSubjectMatch = false
    let isClassAssociation = false
    let isClassGroupAssociation = false
    let isConsecutiveAvoidance = false

    // Criterion 3: Class teacher preference for supervision (highest priority — apply first)
    // Promote class teacher to top when it's a break/supervision period
    if (
      rules.prefer_class_teacher_for_supervision &&
      isBreakOrSupervision &&
      classTeacherId &&
      tid === classTeacherId
    ) {
      score -= 1000
      isClassTeacherForSupervision = true
    }

    // Criterion 1: Subject match
    if (rules.prefer_subject_match) {
      const subjects = teacherSubjects.get(tid)
      if (subjects && subjects.has(subjectId)) {
        score -= 100
        isSubjectMatch = true
      }
    }

    // Criterion 2: Class association
    if (rules.prefer_class_association) {
      const classAssocs = teacherClassAssociations.get(tid)
      const groupAssocs = teacherClassGroupAssociations.get(tid)

      if (classAssocs && classAssocs.has(classSectionId)) {
        // Direct class section match — highest class association rank
        score -= 50
        isClassAssociation = true
      } else if (classGroupId && groupAssocs && groupAssocs.has(classGroupId)) {
        // Same class group match — secondary rank
        score -= 25
        isClassGroupAssociation = true
      }
    }

    // Criterion 4: Fairness — fewest posted assignments on targetDate first, then fewest in week
    const postedCounts = postedAssignmentsByTeacher.get(tid) ?? { daily: 0, weekly: 0 }
    // Add daily count as a tiebreaker (scaled to not override higher-priority criteria)
    score += postedCounts.daily * 0.01
    // Add weekly count as a secondary tiebreaker
    score += postedCounts.weekly * 0.001

    // Criterion 5: Consecutive avoidance
    if (rules.avoid_consecutive && currentRunAssignedTeacherIds.has(tid)) {
      score += 10
      isConsecutiveAvoidance = true
    }

    breakdowns.set(tid, {
      teacher,
      score,
      isSubjectMatch,
      isClassAssociation,
      isClassGroupAssociation,
      isClassTeacherForSupervision,
      isConsecutiveAvoidance,
      dailyPostedCount: postedCounts.daily,
      weeklyPostedCount: postedCounts.weekly,
    })
  }

  // Stable sort: use index as tiebreaker to preserve original order for equal scores
  const indexed = candidates.map((t, i) => ({ teacher: t, index: i }))
  indexed.sort((a, b) => {
    const scoreA = breakdowns.get(a.teacher.id)?.score ?? 0
    const scoreB = breakdowns.get(b.teacher.id)?.score ?? 0
    if (scoreA !== scoreB) return scoreA - scoreB
    return a.index - b.index // stable: preserve original order
  })

  const sorted = indexed.map((x) => x.teacher)

  // Build top 3 breakdowns for the animation
  const topBreakdowns = sorted.slice(0, 3).map((t) => breakdowns.get(t.id)!)

  return { sorted, topBreakdowns }
}

// ============================================================================
// buildMatchReasons
// ============================================================================

/**
 * Convert a CandidateScoreBreakdown into human-readable match reason strings.
 * Used by the progress animation to explain why a teacher was selected.
 */
export function buildMatchReasons(breakdown: CandidateScoreBreakdown): string[] {
  const reasons: string[] = []
  if (breakdown.isClassTeacherForSupervision) reasons.push('Class teacher for supervision')
  if (breakdown.isSubjectMatch) reasons.push('Subject specialist')
  if (breakdown.isClassAssociation) reasons.push('Teaches this class')
  if (breakdown.isClassGroupAssociation) reasons.push('Teaches same class group')
  if (!breakdown.isConsecutiveAvoidance && breakdown.dailyPostedCount === 0) reasons.push('No subs today')
  else if (!breakdown.isConsecutiveAvoidance && breakdown.dailyPostedCount <= 1) reasons.push('Light workload today')
  if (breakdown.isConsecutiveAvoidance) reasons.push('Consecutive period — lower priority')
  if (reasons.length === 0) reasons.push('Available & eligible')
  return reasons
}
